import React, { useEffect, useMemo, useState } from "react";
import { Button, FileIcon, Select, TextInput } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import {
  CLIENT_ALIASES,
  CLIENT_ID_ALIASES,
  ENTITY_ALIASES,
  INVESTOR_ALIASES,
  resolveAttributeKey,
  semanticOf,
  sortGlobalsByRolePriority,
  STATUS_ALIASES,
} from "#common/attributeKeys";
import { attributeValuePlaceholder } from "#common/attributeValues";
import { DateOnlyPicker } from "#common/DateOnlyPicker";
import { canPreviewExtension, DocumentPreview } from "#common/DocumentPreview";
import { isIdShapedDataType } from "#common/graphql/Document";
import { MULTIPART_THRESHOLD_LABEL } from "#helpers/upload-multipart";
import {
  extensionOf,
  isEmptyFile,
  isOversize,
  isTooLargeForBatch,
  MAX_UPLOAD_LABEL,
} from "./files";
import {
  joinMultiValue,
  optionIdsFor,
  pickedLabels,
  refIdList,
  singleRefId,
  splitMultiValue,
} from "./multiSelect";
import {
  ClientSearchField,
  EntitySearchField,
  InvestorPositionSearchField,
} from "./ReferenceSearchField";
import * as styles from "./styles_vanilla.css";
import {
  attributeValueFormatError,
  EMPTY_FILE_MESSAGE,
  isBlankAttributeValue,
  uploadFailureMessage,
  wireAttributeValue,
} from "./uploadErrors";
import type {
  AttrDataType,
  DocumentStatus,
  FieldDef,
  FieldKind,
  GlobalAttribute,
  ReferenceData,
  Space,
  VaultDocument,
  VaultDomain,
} from "#common/types/vault";
import type {
  UploadAttributeValue,
  UploadDocumentModalProps,
  UploadFileDescriptor,
  UploadFileRequest,
  UploadFileResult,
} from "./types";

export type { UploadDocumentModalProps };
export type { UploadOptionSources, InheritedFilters } from "./types";

const SAMPLE_FILES: UploadFileDescriptor[] = [
  { id: "up1", name: "Q2-2026 Capital Call Notice.pdf", size: "1.2 MB", pages: 4 },
  { id: "up2", name: "Fund III — Financial Statements.pdf", size: "3.4 MB", pages: 18 },
];

/**
 * Blob URL for the active file, revoked as soon as the selection moves on.
 * Falls back to undefined (i.e. no inline preview) wherever object URLs aren't
 * available, rather than taking the takeover down with it.
 */
function useObjectUrl(file?: File): string | undefined {
  const url = useMemo(() => {
    if (!file || typeof URL.createObjectURL !== "function") return undefined;
    try {
      return URL.createObjectURL(file);
    } catch {
      return undefined;
    }
  }, [file]);
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );
  return url;
}

type Form = {
  spaceId: string;
  values: Record<string, string>;
  /**
   * Picked reference ids, keyed by attribute key — the names live in `values`.
   * A Multi-select holds every picked option id; every other field holds one.
   */
  refIds: Record<string, string | string[] | undefined>;
};

/**
 * Never generated as a form field: derived from the client / from sharing state.
 * Resolved against the catalog, since a hardcoded key excludes nothing when the
 * environment names the attribute differently (`clientId` vs `client_id`).
 *
 * The Client ID key is only excluded when it is a DISTINCT key from the Client
 * key. When the catalog exposes only `client_id` (no separate `clientId`),
 * `resolveAttributeKey` returns the same slug for both, meaning `client_id` IS
 * the Client reference and must remain visible as a form field.
 */
const derivedKeysFor = (globalLib: GlobalAttribute[]): string[] => {
  const clientKey = resolveAttributeKey(CLIENT_ALIASES, globalLib);
  const clientIdKey = resolveAttributeKey(CLIENT_ID_ALIASES, globalLib);
  return [
    resolveAttributeKey(STATUS_ALIASES, globalLib),
    ...(clientIdKey !== clientKey ? [clientIdKey] : []),
  ];
};

/**
 * The sharing status a new document is stamped with. Read straight from the space
 * because this screen does not offer the control (FEATURE-8) — keeping a
 * never-rendered `status` on the form was one more piece of state that looked
 * editable but wasn't, and only a comment stopped someone deleting it.
 */
const spaceStatusDefault = (space: Space): DocumentStatus =>
  (space.globals.status?.default as DocumentStatus) || "Internal";

/** How one generated field is rendered: its key, display label, and control kind. */
export type UploadFieldSpec = { key: string; label: string; kind: FieldKind };

function resolveSpec(
  aliases: readonly string[],
  label: string,
  kind: FieldKind,
  globalLib: GlobalAttribute[],
): UploadFieldSpec {
  const attr = aliases.map((a) => globalLib.find((g) => g.key === a)).find(Boolean);
  return { key: attr?.key ?? aliases[0], label, kind };
}

/**
 * The Client spec — rendered first among global attribute fields under the
 * Global Attributes section (after Space). Being first is a display convention
 * only; the on-gate follows the same rule as every other global.
 */
export function clientSpecFor(globalLib: GlobalAttribute[]): UploadFieldSpec {
  return resolveSpec(CLIENT_ALIASES, "Client", "select", globalLib);
}

/**
 * Generated global fields: the global attributes THIS SPACE references and turns
 * on (`space.globals[key].on`), in catalog order. This mirrors how space-local
 * attributes are drawn (straight off `space.locals`) — a global's presence on the
 * form now has the same provenance as a local's, driven by the space contract the
 * API supplies rather than a hardcoded set.
 *
 * A global the space does not reference is not drawn. "Document Date" is no
 * different: it appears only when the catalog carries it AND the space turns it
 * on. Required-ness still comes from the space contract too — see `requiredMissing`.
 *
 * Excluded: only the derived attributes (status, Client ID — set by the system,
 * not the filer). Non-derived globals are sorted so Client → Entity → LP always
 * lead in that order, with any remaining globals following in catalog order.
 */
export function globalSpecsFor(globalLib: GlobalAttribute[], space: Space): UploadFieldSpec[] {
  const derived = derivedKeysFor(globalLib);
  return sortGlobalsByRolePriority(
    globalLib
      .filter((a) => space.globals[a.key]?.on)
      .filter((a) => !derived.includes(a.key))
      .map((a) => ({ key: a.key, label: a.name, kind: kindForDataType(a.dataType) })),
    globalLib,
  );
}

/**
 * Resolve the form's string values into the id + type pairs the upload mutation
 * needs. Globals resolve through `globalIdByKey`; space-locals already carry the
 * backend attribute id on the space contract.
 *
 * A global with no id in the map is DROPPED rather than sent with a guessed id —
 * an unbound attributeId would be rejected, or worse, bind to the wrong
 * attribute. `missing` reports those so the caller can warn.
 */
export function buildUploadAttributeValues(
  form: {
    values: Record<string, string>;
    refIds?: Record<string, string | string[] | undefined>;
  },
  space: Space,
  globalLib: GlobalAttribute[],
  globalIdByKey: Record<string, string>,
): { values: UploadAttributeValue[]; missing: string[] } {
  const values: UploadAttributeValue[] = [];
  const missing: string[] = [];

  Object.entries(form.values).forEach(([key, value]) => {
    if (isBlankAttributeValue(value)) return;
    if (key.startsWith("loc:")) {
      const name = key.slice("loc:".length);
      const local = space.locals.find((l) => l.name === name);
      if (local?.id) {
        // Id-shaped locals (Single/Multi-select) carry their picked option ids
        // through to `valueIds` (DV-466/DV-544), same as globals below. A
        // Multi-select contributes every id it holds (DV-565).
        const ids = isIdShapedDataType(local.dataType) ? refIdList(form.refIds?.[key]) : [];
        values.push({
          attributeId: local.id,
          dataType: local.dataType,
          value: wireAttributeValue(local.dataType, value),
          ...(ids.length ? { ids } : {}),
        });
      } else missing.push(name);
      return;
    }
    const attributeId = globalIdByKey[key];
    const attr = globalLib.find((a) => a.key === key);
    if (attributeId) {
      const dataType = attr?.dataType ?? "Text";
      const refId = singleRefId(form.refIds?.[key]);
      // Id-shaped attributes (reference / select) carry their picked ids through
      // to `valueIds` (DV-466); the display name stays in `value` for the
      // empty-value check downstream.
      const ids = isIdShapedDataType(dataType) ? refIdList(form.refIds?.[key]) : [];
      values.push({
        attributeId,
        dataType,
        value:
          dataType === "Number" && refId
            ? numericIdFromReference(refId)
            : wireAttributeValue(dataType, value),
        ...(ids.length ? { ids } : {}),
      });
    } else {
      missing.push(attr?.name ?? key);
    }
  });

  return { values, missing };
}

/** Convert a Relay global id such as `ArenaObjectType:23` to its numeric id. */
function numericIdFromReference(referenceId: string): string {
  if (/^[0-9]+$/.test(referenceId)) return referenceId;
  try {
    const decoded = globalThis.atob(referenceId);
    const numericId = decoded.slice(decoded.lastIndexOf(":") + 1);
    return /^[0-9]+$/.test(numericId) ? numericId : referenceId;
  } catch {
    return referenceId;
  }
}

/**
 * "Capital Call Notices · Fund Admin" — a space qualified by the domain it sits
 * in, so the picker disambiguates same-named spaces across domains. Falls back to
 * the bare name when the domain isn't in the supplied catalog.
 */
export function spaceOptionLabel(space: Space, domains: VaultDomain[]): string {
  const domain = domains.find((d) => d.id === space.domain);
  return domain ? `${space.name} · ${domain.name}` : space.name;
}

/**
 * How one global attribute is rendered. `fields` is an optional presentational
 * override; without it (the backed path, where no `fields` map is supplied)
 * everything is derived from the global-attribute library itself — so labels are
 * the attribute's display name rather than its raw key, and select options come
 * from its value set.
 */
type Control = {
  label: string;
  kind: FieldKind;
  options: string[];
  /**
   * Backend option id for each of `options`, keyed by display value. Set only
   * when the catalog attribute is Single-select/Multi-select — resolves a
   * picked value back to the id `valueIds` requires (DV-466/DV-544).
   */
  optionIdsByValue?: Record<string, string>;
  /** Whether the select takes more than one option (DV-565). */
  multiple?: boolean;
};

function kindForDataType(dataType: AttrDataType): FieldKind {
  if (dataType === "Date") return "date";
  if (dataType === "Text" || dataType === "Number") return "text";
  return "select";
}

/** Closed set for Yes/No — backend `boolean` has no option rows (DV-560). */
const YES_NO_OPTIONS = ["Yes", "No"];

function controlForGlobal(
  spec: UploadFieldSpec,
  values: Record<string, string>,
  globalLib: GlobalAttribute[],
  fields: Record<string, FieldDef>,
  reference: ReferenceData,
  clientKey: string,
): Control {
  const { key } = spec;
  const attr = globalLib.find((a) => a.key === key);
  const override = fields[key];
  // Prefer the catalog's display name, then the screen's own label — never the
  // raw key, which is what leaked through before.
  const label = override?.label ?? attr?.name ?? spec.label;
  // Yes/No maps to `valueBoolean` with no catalog options or reference list —
  // hardcode the closed set (same as DocumentDetail / space-local upload).
  if (attr?.dataType === "Yes/No") {
    return { label, kind: "select", options: YES_NO_OPTIONS };
  }
  // Catalog wins on kind (it knows the real data type); the spec is the fallback
  // for a field the catalog doesn't have.
  const kind = override?.kind ?? (attr ? kindForDataType(attr.dataType) : spec.kind);
  // A fixed value set on the attribute (Single-/Multi-select's own catalog
  // values, carrying their option ids) wins whenever it exists; only fall back
  // to the shared reference list when the catalog has none of its own — an
  // emptiness check on the reference list picked the wrong one once those
  // lists (or a `fields` override) stopped being empty (DV-544).
  const usingCatalogValues = !!(attr?.values?.length || attr?.optionIdsByValue);
  const options =
    kind === "select" && !usingCatalogValues
      ? (override
          ? referenceOptionsFor(values, override, reference, clientKey)
          : referenceListFor(key, values, reference, clientKey)) || []
      : [];
  return {
    label,
    kind,
    options: usingCatalogValues ? (attr?.values ?? []) : options,
    // Only the catalog's own fixed value set carries option ids — the reference
    // lists above are Reference-type attributes, which resolve their id through
    // `refIds` (the search-field pick), not this map.
    optionIdsByValue: usingCatalogValues ? attr?.optionIdsByValue : undefined,
    multiple: attr?.dataType === "Multi-select",
  };
}

/**
 * Shared reference list a `Reference` attribute draws from. Keyed by the
 * attribute's MEANING, not its key: switching on the raw key returned an empty
 * list wherever the environment used a different name (`entity_id` for Entity).
 */
function referenceListFor(
  key: string,
  values: Record<string, string>,
  reference: ReferenceData,
  clientKey: string,
): string[] {
  switch (semanticOf(key)) {
    case "client":
      return reference.clients;
    case "entity": {
      const client = values[clientKey];
      return client
        ? (reference.entities[client] ?? [])
        : Object.values(reference.entities).flat();
    }
    case "investor":
      return reference.investors;
    case "period":
      return reference.periods;
    default:
      break;
  }
  // Not one of the vault-wide attributes — fall back to the by-key lists.
  switch (key) {
    case "fundAdmin":
      return reference.fundAdmins;
    case "assetClass":
      return reference.assetClasses;
    default:
      return [];
  }
}

function referenceOptionsFor(
  values: Record<string, string>,
  f: FieldDef,
  reference: ReferenceData,
  clientKey: string,
): string[] {
  if (f.options) return f.options;
  switch (f.source) {
    case "clients":
      return reference.clients;
    case "entities": {
      const client = values[clientKey];
      return client
        ? (reference.entities[client] ?? [])
        : Object.values(reference.entities).flat();
    }
    case "investors":
      return reference.investors;
    case "periods":
      return reference.periods;
    case "fundAdmins":
      return reference.fundAdmins;
    case "assetClasses":
      return reference.assetClasses;
    default:
      return [];
  }
}

function initForm(space: Space, browseSpace?: string | null): Form {
  const spaceId = browseSpace && space.id === browseSpace ? browseSpace : space.id;
  const values: Record<string, string> = {};
  Object.entries(space.globals).forEach(([k, cfg]) => {
    // `globals` can be sparse — a key the space never configured has no entry.
    if (cfg?.on && cfg.default) values[k] = cfg.default;
  });
  return {
    spaceId,
    values,
    refIds: {},
  };
}

export function UploadDocumentModal({
  initialFiles,
  spaces,
  domains = [],
  referenceSearch = false,
  globalLib,
  globalIdByKey = {},
  fields,
  reference,
  browseSpace = null,
  onClose,
  onAdd,
  onToast,
  onUploadFile,
  onUploadFiles,
  onUploadComplete,
}: UploadDocumentModalProps) {
  const defaultSpace = useMemo(
    () => (browseSpace ? spaces.find((s) => s.id === browseSpace) : null) ?? spaces[0],
    [spaces, browseSpace],
  );

  // Client / Entity / Investor as THIS catalog names them. Resolved once up here
  // because the form stores values under the attribute key, so the cascade logic,
  // the reference fields, and the upload payload all have to agree on it.
  const clientKey = useMemo(() => clientSpecFor(globalLib).key, [globalLib]);
  const entityKey = useMemo(() => resolveAttributeKey(ENTITY_ALIASES, globalLib), [globalLib]);
  const investorKey = useMemo(
    () => resolveAttributeKey(INVESTOR_ALIASES, globalLib),
    [globalLib],
  );

  // The picked set is owned here so removing a file can shrink it in place.
  // Adding to it is the caller's job — the takeover no longer opens a picker.
  const [files, setFiles] = useState<UploadFileDescriptor[]>(() =>
    initialFiles && initialFiles.length ? initialFiles : SAMPLE_FILES,
  );
  const [activeId, setActiveId] = useState(files[0]?.id ?? "");
  const [attempted, setAttempted] = useState(false);
  /** Per-file upload progress, only used on the real-upload path. */
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<Record<string, string>>({});
  /** 0–1 for the file currently being PUT; multipart drives this per part. */
  const [uploadProgress, setUploadProgress] = useState(0);
  /**
   * Files finished in the CURRENT run. Drives the footer's "Uploading X of N",
   * which used to derive X from `uploadedIds` — that only moves once a whole batch
   * resolves, so a 10-file bulk upload sat frozen on "Uploading 1 of 10" for its
   * entire duration while the per-file path it replaced advanced per file.
   */
  const [uploadedCount, setUploadedCount] = useState(0);
  const [uploadedIds, setUploadedIds] = useState<string[]>([]);
  const [forms, setForms] = useState<Record<string, Form>>(() => {
    if (!defaultSpace) return {};
    const init: Record<string, Form> = {};
    files.forEach((f) => {
      init[f.id] = initForm(defaultSpace, browseSpace);
    });
    return init;
  });

  /** Drop a file from the takeover. Removing the last one exits, since there is
      nothing left to file. */
  const removeFile = (fileId: string) => {
    const remaining = files.filter((f) => f.id !== fileId);
    if (remaining.length === 0) {
      onClose();
      return;
    }
    setForms((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    setFiles(remaining);
    if (activeId === fileId) setActiveId(remaining[0].id);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const active = files.find((f) => f.id === activeId) ?? files[0] ?? null;
  const activeOversize = !!active && isOversize(active);
  const multi = files.length > 1;
  // Too large to batch is only a problem while it HAS company: alone this file
  // goes up the single-file multipart path perfectly well (DV-774).
  const activeTooLargeForBatch = !!active && multi && isTooLargeForBatch(active);
  // No point holding a blob URL for a file that can't be uploaded or previewed.
  const activeUrl = useObjectUrl(
    activeOversize || activeTooLargeForBatch ? undefined : active?.source,
  );
  const form = active ? forms[active.id] : null;
  const space = form
    ? (spaces.find((s) => s.id === form.spaceId) ?? defaultSpace)
    : defaultSpace;

  const setSpace = (fileId: string, spaceId: string) => {
    const next = spaces.find((s) => s.id === spaceId) ?? defaultSpace;
    setForms((prev) => ({ ...prev, [fileId]: initForm(next) }));
  };
  const setValue = (fileId: string, key: string, val: string) => {
    setForms((prev) => {
      const f = prev[fileId];
      const values = { ...f.values, [key]: val };
      if (key === clientKey) values[entityKey] = "";
      return { ...prev, [fileId]: { ...f, values } };
    });
  };
  /**
   * Which dependents to clear when a reference role changes. Client owns entity
   * and investor; entity owns investor. Mirrors `CASCADE_BY_ROLE` in
   * DocumentDetail so the upload and detail surfaces cascade the same way —
   * clearing client used to wipe only entity and left a stale investor id on
   * the file.
   */
  const cascadeKeysFor = (key: string): string[] => {
    if (key === clientKey) return [entityKey, investorKey];
    if (key === entityKey) return [investorKey];
    return [];
  };
  /** Record a reference pick: the display name into `values`, the id into `refIds`. */
  const setReference = (
    fileId: string,
    key: string,
    selection: { id?: string; name: string },
  ) => {
    setForms((prev) => {
      const f = prev[fileId];
      const values = { ...f.values, [key]: selection.name };
      const refIds = { ...f.refIds, [key]: selection.id };
      // Only a *genuine* id change cascades. A reference pick fires `onChange`
      // twice for the same id (once with the id as a placeholder name, then
      // with the resolved name); treating the second as a change would wipe a
      // dependent the user had just picked.
      if (selection.id !== singleRefId(f.refIds[key])) {
        for (const depKey of cascadeKeysFor(key)) {
          values[depKey] = "";
          refIds[depKey] = undefined;
        }
      }
      return { ...prev, [fileId]: { ...f, values, refIds } };
    });
  };
  /**
   * Record a multi-select pick: the joined labels into `values` (the shape the
   * read path renders `valueIds` in), every picked option id into `refIds`.
   */
  const setMultiReference = (
    fileId: string,
    key: string,
    labels: string[],
    ids: string[] | undefined,
  ) => {
    setForms((prev) => {
      const f = prev[fileId];
      return {
        ...prev,
        [fileId]: {
          ...f,
          values: { ...f.values, [key]: joinMultiValue(labels) },
          refIds: { ...f.refIds, [key]: ids },
        },
      };
    });
  };

  const requiredMissing = (s: Space, f: Form): string[] => {
    const missing: string[] = [];
    // Required-ness comes from the SPACE CONTRACT, never from the catalog: the
    // form offers the globals this space references, and only those can be
    // required.
    globalSpecsFor(globalLib, s).forEach(({ key }) => {
      if (s.globals[key]?.required && isBlankAttributeValue(f.values[key])) missing.push(key);
    });
    s.locals.forEach((l) => {
      const key = `loc:${l.name}`;
      if (l.required && isBlankAttributeValue(f.values[key])) missing.push(key);
    });
    return missing;
  };

  /** Per-field Number/Date format errors keyed like `errFor` (form keys). */
  const formatErrorsFor = (s: Space, f: Form): Record<string, string> => {
    const out: Record<string, string> = {};
    Object.entries(f.values).forEach(([key, raw]) => {
      if (!raw || !raw.trim()) return;
      let dataType: AttrDataType | undefined;
      if (key.startsWith("loc:")) {
        dataType = s.locals.find((l) => l.name === key.slice("loc:".length))?.dataType;
      } else {
        dataType = globalLib.find((a) => a.key === key)?.dataType;
      }
      if (!dataType) return;
      const err = attributeValueFormatError(dataType, wireAttributeValue(dataType, raw));
      if (err) out[key] = err;
    });
    return out;
  };

  const fileReady = (f: UploadFileDescriptor): boolean => {
    if (isOversize(f)) return false;
    // The picker keeps these out of a batch, but it is an optional collaborator:
    // a consumer staging files itself and passing `initialFiles` reaches the batch
    // API without it. Enforced here too so the modal's own boundary holds.
    if (multi && isTooLargeForBatch(f)) return false;
    const fm = forms[f.id];
    const s = spaces.find((sp) => sp.id === fm.spaceId);
    return (
      !!s &&
      requiredMissing(s, fm).length === 0 &&
      Object.keys(formatErrorsFor(s, fm)).length === 0
    );
  };
  const readyCount = files.filter(fileReady).length;
  const failedCount = Object.keys(uploadErrors).length;
  const allReady = files.length > 0 && readyCount === files.length;

  const buildDoc = (f: UploadFileDescriptor): VaultDocument | null => {
    const fm = forms[f.id];
    const s = spaces.find((sp) => sp.id === fm.spaceId);
    if (!s) return null;
    const attrs: Record<string, string> = {};
    globalSpecsFor(globalLib, s).forEach(({ key }) => {
      if (!isBlankAttributeValue(fm.values[key])) attrs[key] = fm.values[key];
    });
    const local: Record<string, string> = {};
    s.locals.forEach((l) => {
      const v = fm.values[`loc:${l.name}`];
      if (!isBlankAttributeValue(v)) local[l.name] = v;
    });
    const label = attrs.entity || attrs.client || f.name.replace(/\.[a-z]+$/i, "");
    const name = `${label} — ${s.name}${attrs.period ? ` ${attrs.period}` : ""}`;
    return {
      id: crypto.randomUUID(),
      file: extensionOf(f.name) || "pdf",
      name,
      space: s.id,
      domain: s.domain,
      // Sharing status is not settable on this screen; the space's default stands.
      status: spaceStatusDefault(s),
      modified: "Jun 15, 2026",
      attrs,
      local,
      versions: [{ n: "v1", note: "Added to Vault", who: "Dana Liu", date: "Jun 15, 2026" }],
    };
  };

  /** Local-state path: build a presentational document per file and hand it up. */
  const addAllLocally = () => {
    files.forEach((f) => {
      const doc = buildDoc(f);
      if (doc) onAdd(doc);
    });
  };

  /**
   * Validate one file and resolve it to the request the upload API needs, or an
   * error string to show against it. Shared by the single-file and bulk paths so
   * both apply the same source/space/attribute checks and the same missing-id
   * toast.
   */
  const prepareUpload = (
    f: UploadFileDescriptor,
  ): { request: UploadFileRequest } | { error: string } => {
    const fm = forms[f.id];
    const sp = spaces.find((x) => x.id === fm.spaceId);
    if (!f.source) {
      return { error: "This file has no contents to upload — re-add it and try again." };
    }
    if (isEmptyFile(f)) {
      return { error: EMPTY_FILE_MESSAGE };
    }
    if (!sp) {
      return { error: "Pick a space before uploading." };
    }
    const { values, missing } = buildUploadAttributeValues(fm, sp, globalLib, globalIdByKey);
    if (missing.length > 0) {
      onToast?.(
        `Skipped ${missing.length} attribute${missing.length === 1 ? "" : "s"} with no backend id: ${missing.join(", ")}`,
      );
    }
    const formatMessages: string[] = [];
    for (const v of values) {
      const formatError = attributeValueFormatError(v.dataType, v.value);
      if (formatError) formatMessages.push(formatError);
    }
    if (formatMessages.length > 0) {
      return { error: [...new Set(formatMessages)].join(" · ") };
    }
    return { request: { file: f.source, spaceId: sp.id, attributeValues: values } };
  };

  /**
   * Real path: create → PUT bytes → finalize, one file at a time. Sequential on
   * purpose — each upload PUTs the whole file, and a parallel burst of large
   * uploads is worse for both the browser and the storage substrate. A file that
   * fails keeps its error and stays in the takeover so it can be retried; files
   * that already succeeded are not re-uploaded.
   */
  const uploadEachFile = async () => {
    // Narrowed here rather than trusted from `uploadAll`: both handlers are
    // independently optional, and a guard in the caller does not narrow into the
    // callee. The `!` that used to stand in for this turned a caller wiring only
    // `onUploadFiles` into a TypeError rendered as the file's own error text.
    if (!onUploadFile) return;
    const errors: Record<string, string> = {};
    const results: UploadFileResult[] = [];
    const done: string[] = [...uploadedIds];

    for (const f of files) {
      if (done.includes(f.id)) continue;
      const prepared = prepareUpload(f);
      if ("error" in prepared) {
        errors[f.id] = prepared.error;
        continue;
      }
      try {
        setUploadProgress(0);
        const result = await onUploadFile({
          ...prepared.request,
          onProgress: setUploadProgress,
        });
        results.push(result);
        done.push(f.id);
        setUploadedCount((c) => c + 1);
      } catch (e) {
        errors[f.id] = uploadFailureMessage(e);
      }
    }

    setUploadedIds(done);
    setUploadErrors(errors);
    if (Object.keys(errors).length === 0) onUploadComplete?.(results);
  };

  /**
   * Bulk path: hand the whole selection to `onUploadFiles` (one create + one
   * finalize round trip). Any file that fails validation is held back with its
   * error and the batch is not sent, so the user fixes it before retrying. A
   * transport failure marks every attempted file so they all stay for a retry.
   *
   * A batch can also PARTIALLY fail — the backend finalizes per document — so each
   * result is paired back to its file by index and only the files that actually
   * filed are marked uploaded. Same outcome shape as `uploadEachFile`: failures
   * stay in the takeover with their reason, and `onUploadComplete` fires only when
   * every file made it.
   */
  const uploadAsBatch = async () => {
    const errors: Record<string, string> = {};
    const requests: UploadFileRequest[] = [];
    const requestIds: string[] = [];

    for (const f of files) {
      if (uploadedIds.includes(f.id)) continue;
      const prepared = prepareUpload(f);
      if ("error" in prepared) {
        errors[f.id] = prepared.error;
        continue;
      }
      requests.push(prepared.request);
      requestIds.push(f.id);
    }

    // A file that fails validation is held back with its error, but the VALID ones
    // still go up — matching `uploadEachFile`. Bailing on the whole selection gave
    // the same button two behaviours depending on how many files were picked.
    if (requests.length === 0) {
      setUploadErrors(errors);
      // Nothing left to send. With no errors either, every file is already filed
      // (a retry after a complete run), which is a completed upload, not a button
      // that silently does nothing.
      if (Object.keys(errors).length === 0) onUploadComplete?.([]);
      return;
    }

    try {
      setUploadProgress(0);
      const results = await onUploadFiles!(requests, (fraction) =>
        setUploadedCount(Math.round(fraction * requests.length)),
      );
      const filed: string[] = [];
      requestIds.forEach((id, i) => {
        const result = results[i];
        // A missing result is a failure, not a success: the handler owes one entry
        // per request, and assuming success for a file the batch never reported
        // would tell the user it was filed when nothing says so.
        if (!result || result.error) {
          errors[id] = uploadFailureMessage(
            result?.error ?? "The upload reported no result for this file",
          );
          return;
        }
        filed.push(id);
      });
      setUploadedIds([...uploadedIds, ...filed]);
      setUploadErrors(errors);
      if (Object.keys(errors).length === 0) onUploadComplete?.(results);
    } catch (e) {
      const message = uploadFailureMessage(e);
      requestIds.forEach((id) => {
        errors[id] = message;
      });
      setUploadErrors(errors);
    }
  };

  /**
   * Real upload entry point. A selection of 2+ files goes through the bulk API
   * when the caller wired `onUploadFiles`; a lone file (or no bulk handler) keeps
   * the per-file path.
   */
  const uploadAll = async () => {
    if (!onUploadFile && !onUploadFiles) return;
    setUploading(true);
    setUploadedCount(0);
    try {
      // Dispatch on CAPABILITY, not just count: a caller that wired only the bulk
      // handler must take the bulk path even for a single file, or it lands in a
      // per-file path with no per-file handler.
      if (onUploadFiles && (files.length > 1 || !onUploadFile)) {
        await uploadAsBatch();
      } else {
        await uploadEachFile();
      }
    } finally {
      setUploading(false);
    }
  };

  const submitAll = () => {
    if (!allReady) {
      setAttempted(true);
      return;
    }
    if (onUploadFile || onUploadFiles) {
      void uploadAll();
      return;
    }
    addAllLocally();
  };

  const copyToAll = (group: "global" | "local") => {
    if (!active) return;
    const src = forms[active.id];
    let applied = 0;
    setForms((prev) => {
      const next = { ...prev };
      files.forEach((f) => {
        if (f.id === active.id) return;
        // Global Attributes owns Space (DV-561): Copy to all on that section
        // also applies the source file's space — re-init when it differs so the
        // destination picks up that space's contract before global values land.
        let dest =
          group === "global" && next[f.id].spaceId !== src.spaceId
            ? initForm(spaces.find((s) => s.id === src.spaceId) ?? defaultSpace)
            : next[f.id];
        dest = {
          ...dest,
          values: { ...dest.values },
          refIds: { ...dest.refIds },
        };
        Object.entries(src.values).forEach(([k, v]) => {
          const isLocal = k.startsWith("loc:");
          if (group === "local" ? !isLocal : isLocal) return;
          dest.values[k] = v;
          // The picked ids have to ride along with the copied display name, for
          // BOTH groups. A global reference left without its id keeps a client
          // name with nothing behind it, which leaves the entity search unscoped
          // and the chips unclearable. An id-shaped local (Single/Multi-select)
          // left without its ids falls through `buildAttributeValueInput`'s
          // `spec.ids.length > 0` check and writes as `valueText`, which the
          // subgraph rejects — an attribute value must populate exactly one of
          // value_text / value_number / value_date / value_boolean / valueIds
          // (DV-544/DV-565). Keyed off the same group predicate as the value so
          // the destination's (name, ids) pair always matches the source's —
          // copying the whole `refIds` map, as this used to for globals, also
          // dragged `loc:` ids onto files whose local values were never copied.
          const ref = src.refIds[k];
          if (ref === undefined) delete dest.refIds[k];
          else dest.refIds[k] = Array.isArray(ref) ? [...ref] : ref;
        });
        next[f.id] = dest;
        applied += 1;
      });
      return next;
    });
    onToast?.(
      applied > 0
        ? `Copied ${group === "local" ? "space-local" : "global"} attributes to ${applied} other file${applied === 1 ? "" : "s"} — review each before adding`
        : "Nothing to copy attributes to",
    );
  };

  if (spaces.length === 0) {
    return (
      <div className={styles.takeover}>
        <div className={styles.bar}>
          <div className={styles.exit}>
            <Button variant="muted" icon="xmark" iconPosition="left" onClick={onClose}>
              Exit
            </Button>
          </div>
          <h2 className={styles.barTitle}>Add a document to the Vault</h2>
        </div>
        <div className={styles.emptyState}>
          <div className={styles.emptyStateIcon}>
            <Icon type="inbox" size="xx-large" />
          </div>
          <h3 className={styles.emptyStateTitle}>No spaces available</h3>
          <p className={styles.emptyStateText}>
            Create a space in Administration → Spaces before uploading documents.
          </p>
        </div>
      </div>
    );
  }

  if (!active || !form || !space) return null;

  const globalSpecs = globalSpecsFor(globalLib, space);
  const missing = requiredMissing(space, form);
  const formatErrs = formatErrorsFor(space, form);
  const errFor = (key: string) => {
    if (!attempted) return undefined;
    if (missing.includes(key)) return "Required";
    return formatErrs[key];
  };

  const isRequired = (key: string): boolean => {
    if (key.startsWith("loc:")) {
      const name = key.slice("loc:".length);
      return !!space.locals.find((l) => l.name === name)?.required;
    }
    return !!space.globals[key]?.required;
  };
  const labelWithReq = (label: string, key: string) => (isRequired(key) ? `${label} *` : label);

  /** One generated global-attribute field, driven by the attribute library. */
  const renderGlobalField = (spec: UploadFieldSpec) => {
    const k = spec.key;
    const { label, kind, options, optionIdsByValue, multiple } = controlForGlobal(
      spec,
      form.values,
      globalLib,
      fields,
      reference,
      clientKey,
    );
    // Client / Entity / Investor get the shared reference-search components when enabled.
    if (referenceSearch && (k === clientKey || k === entityKey || k === investorKey)) {
      return (
        <div key={k} className={styles.field}>
          <div className={styles.refLabel}>{labelWithReq(label, k)}</div>
          {k === clientKey ? (
            <ClientSearchField
              // `ClientSearch` ignores `value` internally (its input keeps its own
              // state), so remount per file to clear stale display text when the
              // active file — and therefore the form — changes. `selectedName`
              // is what lets the new mount show this file's existing pick.
              //
              // The space is part of the key for the same reason (DV-569):
              // changing it rebuilds the form from scratch, but neither the
              // typeahead's own text nor the preset chip is derived from that
              // form, so without a remount the cleared Client stayed on screen.
              key={`client-${active.id}-${form.spaceId}`}
              label={label}
              space={space}
              value={singleRefId(form.refIds[clientKey])}
              selectedName={form.values[clientKey]}
              onChange={(selection) => setReference(active.id, clientKey, selection)}
            />
          ) : k === entityKey ? (
            <EntitySearchField
              // Same remount trick, plus the client: changing it invalidates the
              // entity, and the typeahead must not keep showing the old one.
              key={`entity-${active.id}-${singleRefId(form.refIds[clientKey]) ?? "none"}`}
              clientId={singleRefId(form.refIds[clientKey])}
              value={singleRefId(form.refIds[entityKey])}
              selectedName={form.values[entityKey]}
              onChange={(selection) => setReference(active.id, entityKey, selection)}
            />
          ) : (
            <InvestorPositionSearchField
              // Remount when the file, client, or entity changes — any of those
              // narrows or resets the available positions.
              key={`investor-${active.id}-${singleRefId(form.refIds[clientKey]) ?? "none"}-${singleRefId(form.refIds[entityKey]) ?? "none"}`}
              clientId={singleRefId(form.refIds[clientKey])}
              entityId={singleRefId(form.refIds[entityKey])}
              value={singleRefId(form.refIds[investorKey])}
              selectedName={form.values[investorKey]}
              onChange={(sel) => setReference(active.id, investorKey, sel)}
            />
          )}
          {errFor(k) && <div className={styles.refError}>{errFor(k)}</div>}
        </div>
      );
    }
    if (kind === "select") {
      // A catalog-backed Single/Multi-select carries option ids (DV-466/DV-544);
      // route the pick through `setReference` so the id lands in `refIds`, same
      // as a Client/Entity pick. Other selects (Yes/No, non-catalog reference
      // lists) have no id and stay on plain `setValue`.
      const onSelect = (v: string | number | string[] | number[]) => {
        const picked = pickedLabels(v);
        if (multiple) {
          setMultiReference(active.id, k, picked, optionIdsFor(picked, optionIdsByValue));
          return;
        }
        const value = picked[0] ?? "";
        if (optionIdsByValue) {
          setReference(active.id, k, { id: optionIdsByValue[value], name: value });
        } else setValue(active.id, k, value);
      };
      return (
        <div key={k} className={styles.field}>
          <Select
            label={labelWithReq(label, k)}
            multiple={multiple}
            value={multiple ? splitMultiValue(form.values[k]) : (form.values[k] ?? "")}
            placeholder="Select…"
            full
            error={errFor(k)}
            onChange={onSelect}
          >
            <Select.Section>
              {options.map((o) => (
                <Select.Option key={o} value={o}>
                  {o}
                </Select.Option>
              ))}
            </Select.Section>
          </Select>
        </div>
      );
    }
    if (kind === "date") {
      return (
        <div key={k} className={styles.field}>
          <span className={styles.fieldLabel}>{labelWithReq(label, k)}</span>
          <DateOnlyPicker
            value={form.values[k] ?? ""}
            errorMessage={errFor(k)}
            onChange={(next) => setValue(active.id, k, next)}
          />
        </div>
      );
    }
    return (
      <div key={k} className={styles.field}>
        <TextInput
          isFullWidth
          label={labelWithReq(label, k)}
          value={form.values[k] ?? ""}
          placeholder={label}
          errorMessage={errFor(k)}
          onChange={(e) => setValue(active.id, k, e.target.value)}
        />
      </div>
    );
  };

  const activeExt = extensionOf(active.name);
  // Only PDFs render inline. Everything else gets an explicit "download to read
  // it" state rather than a preview that would misrepresent the file.
  const canPreview = canPreviewExtension(activeExt);
  const downloadActive = () => {
    if (!activeUrl) return;
    const a = document.createElement("a");
    a.href = activeUrl;
    a.download = active.name;
    a.click();
  };

  return (
    <div
      className={styles.takeover}
      role="dialog"
      aria-modal="true"
      aria-label="Add documents to the Vault"
    >
      <div className={styles.bar}>
        <div className={styles.exit}>
          <Button variant="muted" icon="xmark" iconPosition="left" onClick={onClose}>
            Exit
          </Button>
        </div>
        <h2 className={styles.barTitle}>
          Add {files.length === 1 ? "a document" : `${files.length} documents`} to the Vault
        </h2>
      </div>

      <div className={styles.body}>
        {multi && (
          <div className={styles.fileRail}>
            <div className={styles.railLabel}>{files.length} files</div>
            {files.map((f) => (
              <button
                key={f.id}
                type="button"
                className={f.id === active.id ? styles.fileTab.on : styles.fileTab.off}
                // The failure goes on the BUTTON's own name. An `aria-label` on the
                // inner span still feeds the button's name computation, so the tab
                // announced the filename twice ("one.pdf, one.pdf failed to…").
                aria-label={
                  uploadErrors[f.id]
                    ? `${f.name} — failed to upload: ${uploadErrors[f.id]}`
                    : undefined
                }
                onClick={() => setActiveId(f.id)}
              >
                <Icon type="file" size="small" />
                <span className={styles.fileTabName}>{f.name}</span>
                {/* A bulk upload fails per file, and the error itself only renders
                    for the ACTIVE file — without a marker here the user is told "1
                    of 2 failed" with no way to tell which, short of clicking each. */}
                {uploadErrors[f.id] && (
                  <span className={styles.fileTabFailed} title={uploadErrors[f.id]} aria-hidden>
                    <Icon type="exclamation-triangle" size="small" customColor="#c8421e" />
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className={styles.preview}>
          <div className={styles.docToolbar}>
            <div className={styles.docFile}>
              <FileIcon extension={activeExt || "doc"} size="large" />
              <div>
                <div className={styles.docFname}>{active.name}</div>
                <div className={styles.docFsub}>
                  {active.size}
                  {active.pages != null &&
                    ` · ${active.pages} page${active.pages === 1 ? "" : "s"}`}
                </div>
              </div>
            </div>
            {!activeOversize && canPreview && active.pages != null && (
              <div className={styles.docPageInd}>Page 1 / {active.pages}</div>
            )}
          </div>

          {uploadErrors[active.id] && (
            <div className={styles.uploadError} role="alert">
              <Icon type="exclamation-circle" size="small" />
              <span>{uploadErrors[active.id]}</span>
            </div>
          )}

          {activeOversize || activeTooLargeForBatch ? (
            <div className={styles.tooLarge} role="alert">
              <span className={styles.tooLargeMark} aria-hidden>
                <Icon type="exclamation-triangle" size="large" />
              </span>
              <div className={styles.tooLargeTitle}>
                {activeOversize ? "Document is too large" : "Too large to upload with others"}
              </div>
              <div className={styles.tooLargeSub}>
                {activeOversize ? (
                  <>
                    This file is larger than the {MAX_UPLOAD_LABEL} upload limit. Please remove
                    the file and reduce the file size before uploading.
                  </>
                ) : (
                  <>
                    This file is over {MULTIPART_THRESHOLD_LABEL}, so it can&apos;t be uploaded
                    alongside others. Remove it here and add it on its own.
                  </>
                )}
              </div>
              <Button
                variant="danger"
                icon="trash"
                iconPosition="left"
                onClick={() => removeFile(active.id)}
              >
                Remove file
              </Button>
            </div>
          ) : (
            <DocumentPreview
              // Remount per file so a switch can't leave the previous document's
              // frame on screen while the new object URL settles.
              key={active.id}
              extension={activeExt || "doc"}
              name={active.name}
              url={activeUrl}
              hint="The document will still be filed exactly as uploaded."
              action={
                activeUrl && (
                  <Button
                    variant="neutral"
                    icon="download"
                    iconPosition="left"
                    onClick={downloadActive}
                  >
                    Download
                  </Button>
                )
              }
            />
          )}
        </div>

        <div className={styles.formRail}>
          <div className={styles.attrSection}>
            <div className={styles.sectionHead}>
              <h4 className={styles.sectionTitle}>Global Attributes</h4>
              {multi ? (
                <Button
                  variant="muted"
                  icon="copy"
                  iconPosition="left"
                  onClick={() => copyToAll("global")}
                >
                  Copy to all
                </Button>
              ) : (
                globalSpecs.length > 0 && (
                  <span className={styles.sectionSub}>Shared across every space</span>
                )
              )}
            </div>
            <div className={styles.fieldGrid}>
              <div className={styles.field}>
                <Select
                  label="Space"
                  value={form.spaceId}
                  onChange={(v) => setSpace(active.id, String(v))}
                  full
                >
                  <Select.Section>
                    {spaces.map((s) => (
                      <Select.Option key={s.id} value={s.id}>
                        {spaceOptionLabel(s, domains)}
                      </Select.Option>
                    ))}
                  </Select.Section>
                </Select>
              </div>
              {globalSpecs.map(renderGlobalField)}
            </div>
          </div>

          {space.locals.length > 0 && (
            <div className={styles.attrSection}>
              <div className={styles.sectionHead}>
                <h4 className={styles.sectionTitle}>Space-local attributes</h4>
                {multi ? (
                  <Button
                    variant="muted"
                    icon="copy"
                    iconPosition="left"
                    onClick={() => copyToAll("local")}
                  >
                    Copy to all
                  </Button>
                ) : (
                  <span className={styles.sectionSub}>Specific to {space.name}</span>
                )}
              </div>
              <div className={styles.fieldGrid}>
                {space.locals.map((l) => {
                  const key = `loc:${l.name}`;
                  const full = l.dataType === "Text";
                  const cls = full ? `${styles.field} ${styles.fieldFull}` : styles.field;
                  const isSelect =
                    l.dataType === "Single-select" ||
                    l.dataType === "Multi-select" ||
                    l.dataType === "Yes/No";
                  const opts = l.dataType === "Yes/No" ? YES_NO_OPTIONS : (l.options ?? []);
                  // Yes/No has no backend option ids (it maps to `valueBoolean`,
                  // not `valueIds`) — only a genuine Single/Multi-select local
                  // carries them.
                  const localOptionIds =
                    l.dataType === "Single-select" || l.dataType === "Multi-select"
                      ? l.optionIdsByValue
                      : undefined;
                  const isMulti = l.dataType === "Multi-select";
                  const onSelectLocal = (v: string | number | string[] | number[]) => {
                    const picked = pickedLabels(v);
                    if (isMulti) {
                      setMultiReference(
                        active.id,
                        key,
                        picked,
                        optionIdsFor(picked, localOptionIds),
                      );
                      return;
                    }
                    const value = picked[0] ?? "";
                    if (localOptionIds) {
                      setReference(active.id, key, { id: localOptionIds[value], name: value });
                    } else setValue(active.id, key, value);
                  };
                  if (isSelect) {
                    return (
                      <div key={key} className={cls}>
                        <Select
                          label={labelWithReq(l.name, key)}
                          multiple={isMulti}
                          value={
                            isMulti
                              ? splitMultiValue(form.values[key])
                              : (form.values[key] ?? "")
                          }
                          placeholder="Select…"
                          full
                          error={errFor(key)}
                          onChange={onSelectLocal}
                        >
                          <Select.Section>
                            {opts.map((o) => (
                              <Select.Option key={o} value={o}>
                                {o}
                              </Select.Option>
                            ))}
                          </Select.Section>
                        </Select>
                      </div>
                    );
                  }
                  if (l.dataType === "Date") {
                    return (
                      <div key={key} className={cls}>
                        <span className={styles.fieldLabel}>{labelWithReq(l.name, key)}</span>
                        <DateOnlyPicker
                          value={form.values[key] ?? ""}
                          errorMessage={errFor(key)}
                          onChange={(next) => setValue(active.id, key, next)}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={key} className={cls}>
                      <TextInput
                        isFullWidth
                        label={labelWithReq(l.name, key)}
                        value={form.values[key] ?? ""}
                        placeholder={
                          l.dataType === "Number"
                            ? "0"
                            : attributeValuePlaceholder(l.dataType, l.name)
                        }
                        errorMessage={errFor(key)}
                        onChange={(e) => setValue(active.id, key, e.target.value)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={styles.foot}>
        <div className={styles.footCount}>
          {uploading
            ? `Uploading ${Math.min(uploadedCount + 1, files.length)} of ${files.length}` +
              // Only worth showing mid-file; a completed part reads as 100%.
              (uploadProgress > 0 && uploadProgress < 1
                ? ` — ${Math.round(uploadProgress * 100)}%`
                : "…")
            : failedCount > 0
              ? `${failedCount} of ${files.length} failed to upload`
              : allReady
                ? `All ${files.length} ready to add`
                : `${readyCount} of ${files.length} ready`}
        </div>
        <Button variant="neutral" disabled={uploading} onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          icon="plus"
          iconPosition="left"
          isLoading={uploading}
          onClick={submitAll}
        >
          {failedCount > 0
            ? "Retry upload"
            : `Add ${files.length > 1 ? `${files.length} ` : ""}to Vault`}
        </Button>
      </div>
    </div>
  );
}
